<!-- Step content template (shared between mobile and desktop layouts) -->
<ng-template #stepContent>
  @switch (currentStep) {
    @case (Step.License) {
      <app-eula-step
        [loading]="isLoading"
        (accept)="onAcceptLicense()"
        (goBack)="onBackClick()"
      ></app-eula-step>
    }
    @case (Step.Password) {
      <app-password-step
        [passwordPattern]="passwordPattern"
        [loading]="isLoading"
        [currentPasswordError]="currentPasswordError"
        (changePassword)="onChangePassword($event)"
        (goBack)="onBackClick()"
      ></app-password-step>
    }
    @case (Step.PhoneNumber) {
      <app-phone-step
        [loading]="isLoading"
        [initialCountryCode]="ftlService.data.phone.countryCode"
        [initialPhoneNumber]="ftlService.data.phone.phoneNumber"
        (submitPhone)="onSubmitPhone($event)"
        (remindLater)="ftlService.remindPhoneLater()"
        (doNotAskAgain)="onSkipPhone()"
      ></app-phone-step>
    }
    @case (Step.PhoneConfirmation) {
      <app-phone-confirmation-step
        #phoneConfirmStep
        [loading]="isLoading"
        [hasError]="phoneVerificationError"
        [resendDisabled]="resendDisabled"
        [cooldownSeconds]="cooldownSeconds"
        (verifyCode)="onVerifyPhoneCode($event)"
        (resendCode)="onResendPhoneCode()"
        (remindLater)="ftlService.remindPhoneLater()"
        (doNotAskAgain)="onSkipPhone()"
        (goBack)="ftlService.previousStep()"
      ></app-phone-confirmation-step>
    }
    @case (Step.ContactEmail) {
      <app-email-step
        [loading]="isLoading"
        [initialEmail]="ftlService.data.email.email"
        (submitEmail)="onSubmitEmail($event)"
        (remindLater)="ftlService.remindEmailLater()"
        (doNotAskAgain)="onSkipEmail()"
      ></app-email-step>
    }
    @case (Step.ContactEmailConfirmation) {
      <app-email-confirmation-step
        #emailConfirmStep
        [loading]="isLoading"
        [hasError]="emailVerificationError"
        [resendDisabled]="resendDisabled"
        [cooldownSeconds]="cooldownSeconds"
        (verifyCode)="onVerifyEmailCode($event)"
        (resendCode)="onResendEmailCode()"
        (remindLater)="ftlService.remindEmailLater()"
        (doNotAskAgain)="onSkipEmail()"
        (goBack)="ftlService.previousStep()"
      ></app-email-confirmation-step>
    }
    @case (Step.ContactEmailSuccess) {
      <app-email-success-step
        (next)="ftlService.markEmailConfirmationShown()"
      ></app-email-success-step>
    }
    @case (Step.Speciality) {
      <app-specialty-step
        [loading]="isLoading"
        [initialSpeciality]="ftlService.data.speciality.speciality"
        (submitSpeciality)="onSubmitSpeciality($event)"
        (remindLater)="ftlService.remindSpecialityLater()"
      ></app-specialty-step>
    }
    @case (Step.Templates) {
      <app-templates-step
        [loading]="isLoading"
        (submitTemplates)="onSubmitTemplates($event)"
      ></app-templates-step>
    }
    @case (Step.Done) {
      <app-done-step (complete)="onComplete()"></app-done-step>
    }
  }
</ng-template>

@if (isMobile) {
  <!-- MOBILE LAYOUT -->
  <div class="h-full flex flex-col pt-4 bg-surface-bright">
    <!-- Header -->
    @if (showStepTitle) {
      <div class="flex items-center h-14 shrink-0">
        <button mat-icon-button (click)="onBackClick()" class="text-on-surface ml-1">
          <mat-icon>arrow_back</mat-icon>
        </button>
        <span class="text-primary text-[22px] font-normal" style="font-family: 'DM Sans', sans-serif">
          {{ stepTitle | translate }}
        </span>
      </div>
    }

    <!-- Progress bar -->
    @if (currentStep !== Step.Done && currentStep !== Step.ContactEmailSuccess) {
      <div class="shrink-0 px-5 pt-2 md:pt-7 pb-3">
        <app-progress-indicator
          [totalSteps]="progressSteps.length"
          [currentIndex]="progressIndex"
        ></app-progress-indicator>
      </div>
    }

    <!-- Step content -->
    <div class="flex-1 flex flex-col min-h-0 px-5 pb-5">
      <ng-container *ngTemplateOutlet="stepContent"></ng-container>
    </div>
  </div>
} @else {
  <!-- DESKTOP LAYOUT -->
  <div class="h-full w-full ambient ambient-blue relative overflow-hidden">
    <div class="min-h-full w-full grid grid-rows-[1fr_auto_1fr] justify-items-center">
      <div class="min-h-[88px] w-full flex flex-col items-center justify-end">
        <img [src]="branding.logoSrc" alt="Logo" class="h-10 w-auto mt-4 mb-8 shrink-0" />
      </div>

      <div class="bg-surface-bright rounded-lg flex flex-col
            w-full max-w-[480px]
            h-[min(640px,calc(100dvh-176px))]
            min-h-0
            mx-4 px-6 pb-6 text-left"
        >
        <!-- Progress bar -->
        <div class="pt-9">
          <app-progress-indicator
            [totalSteps]="progressSteps.length"
            [currentIndex]="progressIndex"
          ></app-progress-indicator>
        </div>

        <!-- Step content -->
        <div class="flex-1 min-h-0 flex flex-col py-5">
          <ng-container *ngTemplateOutlet="stepContent"></ng-container>
        </div>
      </div>

      <div class="w-full flex flex-col items-center justify-start">
        <div *ngIf="branding.showPoweredByTpro" class="flex flex-col items-center mt-6">
          <span class="label-small uppercase tracking-[0.15em] text-white/60">{{ 'powered by' | translate }}</span>
          <img src="assets/logo_grey.png" alt="T-Pro" class="h-7 w-auto mt-1.5" />
        </div>
      </div>
    </div>
  </div>
}

<!-- Logout confirmation dialog -->
@if (showLogoutDialog) {
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40" (click)="showLogoutDialog = false">
    <div class="bg-surface-bright rounded-2xl p-6 max-w-sm w-full mx-4 shadow-xl" (click)="$event.stopPropagation()">
      <h3 class="title-large text-on-surface mb-2">{{ 'Logout' | translate }}</h3>
      <p class="body-medium text-on-surface-variant mb-6">{{ 'You will be signed out and will need to log in again' | translate }}</p>
      <div class="flex justify-end gap-3">
        <button mat-button (click)="onLogout()">
          {{ 'Logout' | translate }}
        </button>
        <button mat-button (click)="showLogoutDialog = false">
          {{ 'Continue' | translate }}
        </button>
      </div>
    </div>
  </div>
}

<!-- Error dialog -->
@if (errorMessage) {
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40" (click)="errorMessage = null">
    <div class="bg-surface-bright rounded-2xl p-6 max-w-sm w-full mx-4 shadow-xl" (click)="$event.stopPropagation()">
      <h3 class="title-large text-on-surface mb-2">{{ 'Error' | translate }}</h3>
      <p class="body-medium text-on-surface-variant mb-6">{{ errorMessage | translate }}</p>
      <div class="flex justify-end">
        <button mat-flat-button color="primary" (click)="errorMessage = null; ftlService.resetState()">
          {{ 'OK' | translate }}
        </button>
      </div>
    </div>
  </div>
}
